import { useEffect, useRef } from 'react';

export const HELP_EVENT_TRIGGER = 'bookoll:show-help';
const HELP_EVENT_REGISTER = 'bookoll:help-available';
const HELP_EVENT_UNREGISTER = 'bookoll:help-unavailable';

export const TOUR_ACTIVE_EVENT = 'bookoll:tour-active';
export const TOUR_INACTIVE_EVENT = 'bookoll:tour-inactive';

/**
 * Registra la página actual como "con ayuda disponible": el botón global de
 * ayuda (montado una vez en el layout) se muestra mientras esta página esté
 * activa, y ejecuta `onShowHelp` cuando el usuario lo pulsa. Al desmontar
 * (cambiar de página) se desregistra automáticamente.
 *
 * `enabled` permite ocultar el botón en estados donde la página no tiene
 * tour que enseñar (p. ej. el Dashboard cuando muestra el checklist de
 * configuración inicial en vez del dashboard real).
 *
 * Uso en cualquier página con tour Joyride:
 *   const [runTour, setRunTour] = useState(false);
 *   usePageHelp(() => setRunTour(true));
 */
export function usePageHelp(onShowHelp: () => void, enabled = true) {
  const handlerRef = useRef(onShowHelp);
  handlerRef.current = onShowHelp;

  useEffect(() => {
    if (!enabled) return;

    window.dispatchEvent(new Event(HELP_EVENT_REGISTER));

    const handler = () => handlerRef.current();
    window.addEventListener(HELP_EVENT_TRIGGER, handler);

    return () => {
      window.removeEventListener(HELP_EVENT_TRIGGER, handler);
      window.dispatchEvent(new Event(HELP_EVENT_UNREGISTER));
    };
  }, [enabled]);
}

/**
 * Oculta los botones globales (ayuda + notificaciones) mientras el tour
 * Joyride de la página está activo, para que no tapen ni distraigan del
 * recorrido guiado. Se usa junto a usePageHelp, pasando el mismo `runTour`:
 *
 *   const [runTour, setRunTour] = useState(false);
 *   usePageHelp(() => setRunTour(true));
 *   useTourVisibility(runTour);
 */
export function useTourVisibility(runTour: boolean) {
  useEffect(() => {
    window.dispatchEvent(
      new Event(runTour ? TOUR_ACTIVE_EVENT : TOUR_INACTIVE_EVENT)
    );

    // Si la página se desmonta con el tour todavía abierto (navegación a
    // mitad de tour), aseguramos que los botones vuelvan a aparecer.
    return () => {
      if (runTour) window.dispatchEvent(new Event(TOUR_INACTIVE_EVENT));
    };
  }, [runTour]);
}